import { computed } from 'vue';
import { useInitUpc, useUpc } from '@nuxt-common/media-card-large/app/composables/useUpc';
import { useDataStore } from '@nuxt-common/media-card-large/app/composables/useVuexStore';
import { UPC } from '@nuxt-common/media-card-large/app/stores/types';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import { useProductType } from '~/composables/useProductType';
import { toTraitId } from '../utils/utils';
import type { ParsedColors } from '~/declaration/traits';
import type { ParsedLatchkeys } from '../declaration/latchkey';

function useInitEachUpc(
  id: string,
  latchkeyNames: LatchkeyName[],
  latchkeyUpcMap: Record<string, ParsedColors>,
  isBopsUpcAvailabilityUpdateEnabled?: boolean,
) {
  const store = useDataStore(UPC);

  latchkeyNames.forEach((latchkeyName) => {
    const traitId = toTraitId(id, latchkeyName);
    const existingData = store.get?.(traitId) as Record<string, unknown> | undefined;

    const shouldPreventOverwrite = isBopsUpcAvailabilityUpdateEnabled && existingData?.isBopsUpcAvailabilityUpdateEnabled;
    // Prevent overwriting the store with raw product data if it has already been updated with BOPS availability overrides
    if (!shouldPreventOverwrite) {
      const upcMap = latchkeyUpcMap[latchkeyName];
      useInitUpc({ id: traitId, data: upcMap });
    }
  });
}

export default function useLatchkeyUpc(id: string, selectedLatchkey: Ref<LatchkeyName>, { latchkeyNames, latchkeyUpcMap }: ParsedLatchkeys) {
  const { isConsolidatedLatchKey } = useProductType();
  const { killswitches } = useKillswitches();

  if (isConsolidatedLatchKey) {
    useInitEachUpc(id, latchkeyNames, latchkeyUpcMap, killswitches.isBopsUpcAvailabilityUpdateEnabled);
    const traitId = computed(() => toTraitId(id, selectedLatchkey.value));
    const { hasSelectedUpcId, hasSelectedUpcNumber, selectedUpcNumber, selectedUpcId, selectedUpcIdOrNull, upcData, isMultiUpc } = useUpc(traitId);

    return {
      hasSelectedUpcId,
      hasSelectedUpcNumber,
      selectedUpcNumber,
      selectedUpcId,
      selectedUpcIdOrNull,
      upcData,
      isMultiUpc,
    };
  }

  return {
    hasSelectedUpcId: computed(() => false),
    hasSelectedUpcNumber: computed(() => false),
    selectedUpcNumber: computed(() => ''),
    selectedUpcId: computed(() => undefined),
    selectedUpcIdOrNull: computed(() => null),
    upcData: computed(() => ({})),
    isMultiUpc: computed(() => false),
  };
}
